import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CaretUpDownIcon, CheckIcon, PlusIcon } from '@phosphor-icons/react';
import { useTenant } from '@/tenant/TenantProvider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
} from '@/ui';
import { cn } from '@/lib/cn';

function PlanTag({ plan, onField }: { plan: string | null | undefined; onField?: boolean }) {
  const { t } = useTranslation('shell');
  if (!plan) return null;
  if (onField)
    // On the blue field (sidebar): the plan reads as a saffron word, as in the A3 mockup.
    return (
      <span
        className={cn(
          'text-[0.625rem] font-extrabold uppercase tracking-[0.08em]',
          plan === 'pro' ? 'text-field-accent' : 'text-on-field-subtle',
        )}
      >
        {t(`plan.${plan}`)}
      </span>
    );
  return (
    <span
      className={cn(
        'rounded-[4px] px-1 text-[0.625rem] font-bold uppercase tracking-[0.08em]',
        plan === 'pro' ? 'bg-accent-soft text-accent-ink' : 'bg-sunken text-ink-muted',
      )}
    >
      {t(`plan.${plan}`)}
    </span>
  );
}

export function TenantSwitcher() {
  const { t } = useTranslation('shell');
  const { current, memberships, tenantId, switchTenant } = useTenant();
  const navigate = useNavigate();
  if (!current) return <Skeleton className="h-[3.25rem] w-full rounded-lg" />;
  const active = memberships.filter((m) => m.status === 'active');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        // Lives in the sidebar (brand field): a raised chip with a white-14% edge, saffron initial.
        className="group flex w-full items-center gap-2.5 rounded-lg bg-field-raised px-2.5 py-2.5 text-left shadow-[inset_0_0_0_1px_var(--gh-field-line)] transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-field-focus data-[state=open]:bg-white/10"
        aria-label={t('switchBusiness')}
      >
        <span className="inline-flex size-8.5 shrink-0 items-center justify-center rounded-md bg-field-accent text-[0.95rem] font-black text-on-accent">
          {current.tenant.name.slice(0, 1).toLocaleUpperCase('el-GR')}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[0.84375rem] font-bold leading-tight text-on-field">
            {current.tenant.name}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5 text-xs text-on-field-subtle">
            <PlanTag plan={current.plan?.key} onField />
            <span className="truncate">{t(`role.${current.role}`)}</span>
          </span>
        </span>
        <CaretUpDownIcon className="size-4 shrink-0 text-on-field-subtle group-hover:text-on-field" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-60"
      >
        <DropdownMenuLabel>{t('businesses')}</DropdownMenuLabel>
        {active.map((m) => (
          <DropdownMenuItem
            key={m.tenantId}
            onSelect={() => m.tenantId !== tenantId && switchTenant(m.tenantId)}
          >
            <span className="min-w-0 flex-1 truncate">{m.tenantName}</span>
            <PlanTag plan={m.planKey} />
            {m.tenantId === tenantId && <CheckIcon weight="bold" className="!text-primary-ink" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<PlusIcon />} onSelect={() => navigate('/setup?new=1')}>
          {t('newBusiness')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
